<template>
  <div class="auth-container">
    <!-- View Toggle Switch - Top Left -->
    <div v-if="!isLoggedIn" class="view-toggle-container">
      <div class="toggle-switch">
        <button
          :class="['toggle-btn', { active: mode === 'login' }]"
          @click="switchToLogin"
        >
          Log In
        </button>
        <button
          :class="['toggle-btn', { active: mode === 'signup' }]"
          @click="switchToSignup"
        >
          Sign Up
        </button>
      </div>
    </div>

    <div v-if="isLoggedIn" class="main-content">
      <h1 class="title">You are logged in</h1>
      <div class="auth-card">
        <p class="welcome-text">Welcome, <strong>{{ userId }}</strong></p>
        <div class="actions">
          <base-button @click="logout">Logout</base-button>
        </div>
      </div>
    </div>

    <div v-else class="main-content">
      <h1 class="title">{{ mode === 'login' ? 'Welcome Back' : 'Create Account' }}</h1>

      <!-- Unified Dialog for Loading and Error - Smooth transition -->
      <modern-dialog 
        :show="isLoading || !!error" 
        :title="isLoading ? 'Authenticating...' : 'Error'" 
        type="info"
        @close="handleError"
        :close-on-click-outside="!isLoading"
        :fixed="isLoading"
        :show-actions="!isLoading"
      >
        <p v-if="isLoading">Please wait, it will take a couple of seconds...</p>
        <p v-else>{{ error }}</p>
      </modern-dialog>

      <!-- Verification Code Dialog -->
      <modern-dialog
        :show="showVerificationDialog"
        title="Email Verification"
        type="success"
        @close="closeVerificationDialog"
        :close-on-click-outside="false"
        :large="true"
      >
        <template #default>
          <div class="verification-dialog">
            <p class="dialog-subtitle">Enter the 6-digit code sent to your email</p>
            <div class="digit-inputs">
              <input
                v-for="(digit, index) in verificationCode"
                :key="index"
                type="text"
                maxlength="1"
                v-model="verificationCode[index]"
                @input="handleDigitInput(index, $event)"
                :ref="(el: any) => setDigitInputRef(index, el)"
              />
            </div>
            <p v-if="verificationError" class="errors">{{ verificationError }}</p>
            <div class="resend-row">
              <p>
                Didn't receive the confirmation code?
                <a href="#" @click.prevent="resendCode" :class="{ disabled: isLoading }">Resend</a>
              </p>
            </div>
          </div>
        </template>
        <template #actions>
          <base-button @click="submitVerificationCode">Submit</base-button>
          <base-button mode="flat" @click="closeVerificationDialog">Cancel</base-button>
        </template>
      </modern-dialog>

      <!-- Forgot Password Dialog -->
      <modern-dialog
        :show="showForgotPasswordDialog"
        title="Reset Password"
        type="success"
        @close="closeForgotPasswordDialog"
        :close-on-click-outside="false"
        :large="true"
      >
        <template #default>
          <div class="forgot-password-dialog">
            <div v-if="!resetPasswordStep">
              <p class="dialog-subtitle">Enter your email to receive a reset code</p>
              <input
                type="email"
                v-model="resetEmail"
                placeholder="Enter your email"
              />
            </div>
            <div v-else>
              <p class="dialog-subtitle">Enter the reset code and your new password</p>
              <input
                type="text"
                v-model="resetCode"
                placeholder="Enter reset code"
                autocomplete="off"
              />
              <input
                type="password"
                v-model="newPassword"
                placeholder="Enter new password"
                autocomplete="new-password"
              />
              <input
                type="password"
                v-model="confirmNewPassword"
                placeholder="Confirm new password"
                autocomplete="new-password"
              />
            </div>
          </div>
        </template>
        <template #actions>
          <base-button v-if="!resetPasswordStep" @click="requestPasswordReset">Send Reset Code</base-button>
          <base-button v-else @click="confirmPasswordReset">Confirm</base-button>
          <base-button mode="flat" @click="closeForgotPasswordDialog">Cancel</base-button>
        </template>
      </modern-dialog>

      <div class="auth-card">
        <form @submit.prevent="submitForm" class="auth-form">
          <div class="form-fields">
            <div v-if="mode === 'signup'" class="signup-row">
              <div class="form-field" :class="{ invalid: nameValidity === 'invalid' }">
                <label for="name">First Name</label>
                <input
                  type="text"
                  id="name"
                  v-model.trim="name"
                  @blur="validateInput('name', name)"
                  placeholder="Enter your first name"
                />
                <p v-if="nameValidity === 'invalid'" class="errors">Please enter your First Name</p>
              </div>

              <div class="form-field" :class="{ invalid: lastNameValidity === 'invalid' }">
                <label for="last_name">Last Name</label>
                <input
                  type="text"
                  id="last_name"
                  v-model.trim="last_name"
                  @blur="validateInput('lastName', last_name)"
                  placeholder="Enter your last name"
                />
                <p v-if="lastNameValidity === 'invalid'" class="errors">Please enter your Last Name</p>
              </div>
            </div>

            <div class="form-field" :class="{ invalid: emailValidity === 'invalid' }">
              <label for="email">E-mail</label>
              <input
                type="email"
                id="email"
                v-model.trim="email"
                @blur="validateInput('email', email)"
                placeholder="name@example.com"
              />
              <p v-if="emailValidity === 'invalid'" class="errors">{{ emailErrorMessage }}</p>
            </div>

            <div class="form-field" :class="{ invalid: passwordValidity === 'invalid' }">
              <label for="password">Password</label>
              <input
                type="password"
                id="password"
                v-model.trim="password"
                @blur="validateInput('password', password)"
                placeholder="Enter your password"
              />
              <p v-if="passwordValidity === 'invalid'" class="errors">
                Password must be at least 8 characters, include number, special, upper and lower case.
              </p>
            </div>
          </div>

          <div v-if="mode === 'login'" class="forgot-password-row">
            <a href="#" @click.prevent="openForgotPasswordDialog" class="forgot-password-link">
              Forgot Password?
            </a>
          </div>

          <div class="form-actions">
            <base-button id="login" class="primary-btn">{{ submitButtonCaption }}</base-button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, nextTick } from 'vue';
import { useStore } from '@/composables/core/useStore';
import { useRouter } from 'vue-router';
import { useNotification } from '@/composables/ui/useNotification';
import { fbGetFcmToken, setFcmTokenToTheUserDbEntity } from '../../components/firebase/firebase';
import { getEmailValidationError } from '@/utils/emailValidation';

type ValidityState = 'pending' | 'valid' | 'invalid';

export default defineComponent({
  name: 'UserAuth',
  setup() {
    const store = useStore();
    const router = useRouter();
    const notification = useNotification();

    // Form state
    const email = ref('');
    const password = ref('');
    const formIsValid = ref(true);
    const mode = ref<'login' | 'signup'>('login');
    const isLoading = ref(false);
    const error = ref<string | null>(null);
    const name = ref('');
    const last_name = ref('');

    // Validation flags
    const nameValidity = ref<ValidityState>('pending');
    const lastNameValidity = ref<ValidityState>('pending');
    const emailValidity = ref<ValidityState>('pending');
    const emailErrorMessage = ref('');
    const passwordValidity = ref<ValidityState>('pending');

    // Verification dialog
    const showVerificationDialog = ref(false);
    const verificationCode = reactive(['', '', '', '', '', '']);
    const verificationError = ref<string | null>(null);

    // Forgot password
    const showForgotPasswordDialog = ref(false);
    const resetEmail = ref('');
    const resetCode = ref('');
    const newPassword = ref('');
    const confirmNewPassword = ref('');
    const resetPasswordStep = ref(false);

    // Template refs for digit inputs
    const digitInputRefs: Record<number, HTMLInputElement | null> = {};
    const setDigitInputRef = (index: number, el: HTMLInputElement | null) => {
      digitInputRefs[index] = el;
    };

    // Computed
    const submitButtonCaption = computed(() => mode.value === 'login' ? 'Log In' : 'Sign Up');
    const isLoggedIn = computed(() =>
      store.getters['auth/isAuthenticated'] || !!store.getters['auth/token']
    );
    const userId = computed(() => store.getters['auth/userId'] as string || '');

    // Validation methods
    const validateEmail = (val: string): boolean => {
      const error = getEmailValidationError(val);
      if (error) {
        emailErrorMessage.value = error;
        emailValidity.value = 'invalid';
        return false;
      }
      emailValidity.value = 'valid';
      return true;
    };

    const validatePassword = (val: string): boolean => {
      const hasNumber = /\d/.test(val);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>_]/.test(val);
      const hasUppercase = /[A-Z]/.test(val);
      const hasLowercase = /[a-z]/.test(val);

      if (!val || val.length < 8 || !hasNumber || !hasSpecialChar || !hasUppercase || !hasLowercase) {
        passwordValidity.value = 'invalid';
        return false;
      }
      passwordValidity.value = 'valid';
      return true;
    };

    const validateName = (val: string): boolean => {
      if (!val || val.trim() === '') {
        nameValidity.value = 'invalid';
        return false;
      }
      nameValidity.value = 'valid';
      return true;
    };

    const validateLastName = (val: string): boolean => {
      if (!val || val.trim() === '') {
        lastNameValidity.value = 'invalid';
        return false;
      }
      lastNameValidity.value = 'valid';
      return true;
    };

    const validateInput = (dataType: string, input: string): void => {
      let result: ValidityState = input === '' ? 'invalid' : 'valid';
      switch (dataType) {
        case 'name': nameValidity.value = result; break;
        case 'lastName': lastNameValidity.value = result; break;
        case 'email': validateEmail(input); break;
        case 'password':
          passwordValidity.value = result;
          if (input.length < 6) passwordValidity.value = 'invalid';
          break;
      }
    };

    // FCM token
    const getFcmToken = async (): Promise<void> => {
      try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          console.log('Unable to get permission to notify.');
          return;
        }
        console.log('Notification permission granted.');
        const fcmToken = await fbGetFcmToken();
        if (fcmToken) {
          console.log('This is current FCM token:', fcmToken);
          const userUid = localStorage.getItem('userId');
          if (userUid) {
            setFcmTokenToTheUserDbEntity(userUid, fcmToken);
          }
        } else {
          console.log('No registration token available. Request permission to generate one.');
        }
      } catch (err) {
        console.log('An error occurred while retrieving FCM token. ', err);
      }
    };

    // Form submission
    const submitForm = async (): Promise<void> => {
      formIsValid.value = true;

      if (!validateEmail(email.value)) { formIsValid.value = false; return; }
      if (!validatePassword(password.value)) { formIsValid.value = false; return; }

      if (mode.value === 'signup') {
        if (!validateName(name.value)) { formIsValid.value = false; return; }
        if (!validateLastName(last_name.value)) { formIsValid.value = false; return; }
      }

      isLoading.value = true;

      try {
        if (mode.value === 'login') {
          await store.dispatch('auth/login', { email: email.value, password: password.value });
          await getFcmToken();
          await nextTick();
          await router.replace('/dashboard');
        } else {
          await store.dispatch('auth/signup', {
            email: email.value,
            password: password.value,
            name: name.value,
            lastName: last_name.value,
          });
          showVerificationDialog.value = true;
        }
        isLoading.value = false;
      } catch (err: any) {
        isLoading.value = false;
        error.value = err?.message || err || 'An error occurred. Please try again.';
      }
    };

    // Digit input handling
    const handleDigitInput = (index: number, event: Event): void => {
      const target = event.target as HTMLInputElement;
      const value = target.value;
      if (!/^\d$/.test(value)) {
        verificationError.value = 'Please enter only digits.';
        verificationCode[index] = '';
        return;
      }
      verificationError.value = null;
      verificationCode[index] = value;

      if (index < 5 && value) {
        const nextInput = digitInputRefs[index + 1];
        if (nextInput) nextInput.focus();
      }
    };

    // Verification
    const submitVerificationCode = async (): Promise<void> => {
      if (verificationCode.some((digit) => digit === '')) {
        verificationError.value = 'Please fill in all 6 digits.';
        return;
      }

      const code = verificationCode.join('');
      try {
        await store.dispatch('auth/confirmSignUp', { username: email.value, code });
        showVerificationDialog.value = false;
        notification.success('Verified', 'Verification successful!');

        await store.dispatch('auth/login', { email: email.value, password: password.value });
        await getFcmToken();
        await nextTick();
        await router.replace('/dashboard');
      } catch {
        verificationError.value = 'Invalid verification code. Please try again.';
      }
    };

    const closeVerificationDialog = (): void => {
      showVerificationDialog.value = false;
    };

    const resendCode = async (): Promise<void> => {
      isLoading.value = true;
      try {
        await store.dispatch('auth/resendConfirmationCode', { username: email.value });
        notification.success('Code Sent', 'Confirmation code resent. Please check your email.');
      } catch (err) {
        console.error('Error resending confirmation code:', err);
        notification.error('Error', 'Failed to resend confirmation code. Please try again.');
      } finally {
        isLoading.value = false;
      }
    };

    // Error handling
    const handleError = (): void => { error.value = null; };

    // Auth actions
    const logout = async (): Promise<void> => {
      await store.dispatch('auth/logout');
      await new Promise(resolve => setTimeout(resolve, 100));
      window.location.href = '/auth';
    };

    const switchToLogin = (): void => { mode.value = 'login'; };
    const switchToSignup = (): void => { mode.value = 'signup'; };

    // Forgot password
    const openForgotPasswordDialog = (): void => { showForgotPasswordDialog.value = true; };

    const closeForgotPasswordDialog = (): void => {
      showForgotPasswordDialog.value = false;
      resetEmail.value = '';
      resetCode.value = '';
      newPassword.value = '';
      confirmNewPassword.value = '';
      resetPasswordStep.value = false;
    };

    const requestPasswordReset = async (): Promise<void> => {
      isLoading.value = true;
      try {
        await store.dispatch('auth/resetPasswordRequest', { username: resetEmail.value });
        notification.success('Code Sent', 'Reset code sent to your email.');
        resetPasswordStep.value = true;
        resetCode.value = '';
        newPassword.value = '';
        confirmNewPassword.value = '';
      } catch (err) {
        console.error('Error requesting password reset:', err);
        notification.error('Error', 'Failed to send reset code. Please try again.');
      } finally {
        isLoading.value = false;
      }
    };

    const confirmPasswordReset = async (): Promise<void> => {
      if (newPassword.value !== confirmNewPassword.value) {
        notification.error('Mismatch', 'Passwords do not match.');
        return;
      }

      isLoading.value = true;
      try {
        await store.dispatch('auth/resetPasswordConfirm', {
          username: resetEmail.value,
          code: resetCode.value,
          newPassword: newPassword.value,
        });
        notification.success('Password Reset', 'Password reset successful. You can now log in with your new password.');
        closeForgotPasswordDialog();
      } catch (err) {
        console.error('Error confirming password reset:', err);
        notification.error('Error', 'Failed to reset password. Please try again.');
      } finally {
        isLoading.value = false;
      }
    };

    return {
      email, password, formIsValid, mode, isLoading, error,
      name, last_name,
      nameValidity, lastNameValidity, emailValidity, emailErrorMessage, passwordValidity,
      showVerificationDialog, verificationCode, verificationError,
      showForgotPasswordDialog, resetEmail, resetCode, newPassword, confirmNewPassword, resetPasswordStep,
      submitButtonCaption, isLoggedIn, userId,
      setDigitInputRef,
      submitForm, validateInput, handleError,
      handleDigitInput, submitVerificationCode, closeVerificationDialog, resendCode,
      logout, switchToLogin, switchToSignup,
      openForgotPasswordDialog, closeForgotPasswordDialog, requestPasswordReset, confirmPasswordReset,
    };
  },
});
</script>

<style scoped>
/* Page layout (aligned with Dashboard/Settings) */
.auth-container {
  display: flex;
  flex-direction: column;
  background: var(--color-bg-primary);
  position: relative;
}

/* View Toggle Switch - Top Left Corner */
.view-toggle-container {
  position: absolute;
  top: var(--spacing-4);
  left: var(--spacing-4);
  z-index: 100;
  background: var(--glass-bg);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-bg-border);
  box-shadow: var(--shadow-md);
  backdrop-filter: blur(10px);
}

.toggle-switch {
  display: flex;
  gap: 0;
  background: var(--color-bg-light);
  border-radius: var(--radius-sm);
  padding: 2px;
  width: fit-content;
}

.toggle-btn {
  padding: var(--spacing-2) var(--spacing-4);
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: var(--transition-base);
  white-space: nowrap;
}

.toggle-btn:hover {
  color: var(--color-text-primary);
  background: var(--color-white-alpha-50);
}

.toggle-btn.active {
  background: var(--color-primary);
  color: white;
  box-shadow: var(--shadow-primary-sm);
}

.main-content {
  flex: 1;
  max-width: 650px;
  margin: 0 auto;
  padding: var(--spacing-12) var(--spacing-8);
  width: 100%;
}

.title {
  text-align: center;
  margin-bottom: var(--spacing-6);
  font-size: var(--font-size-3xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
}

/* Card */
.auth-card {
  background: var(--glass-bg);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-2xl);
  padding: var(--spacing-10);
  border: 1px solid var(--color-white-alpha-80);
  backdrop-filter: blur(10px);
}

.welcome-text {
  text-align: center;
  color: var(--color-text-secondary);
  margin: 0 0 var(--spacing-4) 0;
}

.actions {
  display: flex;
  justify-content: center;
}

/* Form */
.auth-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-5);
}

.signup-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-6);
}

.form-field {
  display: flex;
  flex-direction: column;
}

.form-field.invalid input {
  color: var(--color-error);
  background: transparent;
}

label {
  font-weight: var(--font-weight-semibold);
  margin-bottom: var(--spacing-1);
  color: var(--color-text-primary);
  font-size: var(--font-size-md);
}

/* Override Bootstrap input styles completely */
.auth-form input,
.auth-form textarea {
  display: block !important;
  width: 100% !important;
  font: inherit !important;
  border: none !important;
  padding: var(--spacing-3) 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  color: var(--color-text-primary) !important;
  transition: var(--transition-base) !important;
  box-shadow: none !important;
  height: auto !important;
  line-height: normal !important;
}

.auth-form input:focus,
.auth-form textarea:focus {
  outline: none !important;
  box-shadow: none !important;
  border: none !important;
  background: transparent !important;
  background-color: transparent !important;
  color: var(--color-text-primary) !important;
}

.errors {
  margin-top: var(--spacing-1);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-error);
}

/* Forgot password row - separate from password field */
.forgot-password-row {
  text-align: right;
  margin-top: calc(-1 * var(--spacing-2));
  margin-bottom: var(--spacing-2);
}

.forgot-password-link {
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  text-decoration: none;
  transition: color 0.3s ease;
}

.forgot-password-link:hover {
  color: var(--color-primary);
  text-decoration: underline;
}

.form-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: var(--spacing-6);
}

/* Primary action button (override component styles locally) */
.form-actions :deep(button),
.form-actions :deep(.primary-btn) {
  min-width: 200px;
  padding: 0.9rem var(--spacing-10);
  background: var(--color-primary) !important;
  border: none !important;
  color: white !important;
  border-radius: 50px !important;
  font-weight: var(--font-weight-semibold) !important;
  font-size: var(--font-size-base) !important;
  transition: var(--transition-base) !important;
  box-shadow: var(--shadow-primary-md) !important;
}

.form-actions :deep(button):hover,
.form-actions :deep(.primary-btn):hover {
  background: var(--color-primary-dark) !important;
  transform: translateY(-2px);
  box-shadow: var(--shadow-primary-lg) !important;
}

/* Verification dialog */
.verification-dialog {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-3);
}

.dialog-subtitle {
  color: var(--color-text-secondary);
  margin: 0 0 var(--spacing-1) 0;
}

.digit-inputs {
  display: flex;
  gap: var(--spacing-2);
}

.digit-inputs input {
  width: 2.25rem;
  height: 2.75rem;
  text-align: center;
  font-size: var(--font-size-lg);
  border: 1px solid var(--color-bg-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-primary);
  padding: var(--spacing-2);
  transition: border-color 0.3s ease;
}

.digit-inputs input:focus {
  border-color: var(--color-primary);
}

.resend-row {
  margin-top: var(--spacing-1);
}

.disabled {
  pointer-events: none;
  opacity: 0.6;
}

/* Forgot password */
.forgot-password-dialog {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
}

.forgot-password-dialog input {
  width: 100%;
  padding: var(--spacing-3);
  border: 1px solid var(--color-bg-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-primary);
  transition: border-color 0.3s ease;
}

.forgot-password-dialog input:focus {
  border-color: var(--color-primary);
}

/* Responsive Design */
@media (max-width: 768px) {
  .view-toggle-container {
    top: var(--spacing-2);
    left: var(--spacing-2);
  }

  .main-content {
    padding: var(--spacing-8) var(--spacing-6);
  }

  .auth-card {
    padding: var(--spacing-8);
  }
}

@media (max-width: 480px) {
  .main-content {
    padding: var(--spacing-6) var(--spacing-4);
  }

  .auth-card {
    padding: var(--spacing-6);
    border-radius: var(--radius-lg);
  }

  .signup-row {
    grid-template-columns: 1fr;
  }

  .title {
    font-size: var(--font-size-2xl);
  }

  .form-actions :deep(button),
  .form-actions :deep(.primary-btn) {
    min-width: 100%;
    padding: 0.85rem var(--spacing-8);
  }
}
</style>
